// PrivateRoute component to protect routes based on user role
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { UserRole } from '../../../types/restaurant';

interface PrivateRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

// Utility function to get default route based on user role
const getDefaultRouteForRole = (role: UserRole | null): string => {
  switch (role) {
    case 'super_admin':
      return '/super-admin';
    case 'owner':
    case 'admin':
      return '/admin';
    case 'staff':
      return '/mesas';
    default:
      return '/login';
  }
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const location = useLocation();
  const { user, userRole, isLoading } = useRestaurant();

  // Show loading state while Firebase session is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-600">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  // If no user is logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If allowedRoles is specified, check if user's role is allowed
  if (allowedRoles && userRole) {
    const hasAllowedRole = allowedRoles.includes(userRole);
    if (!hasAllowedRole) {
      // Redirect to default route for user's role
      return <Navigate to={getDefaultRouteForRole(userRole)} replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute; 